import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import _ from 'lodash';
import { AgGridReact } from 'ag-grid-react';
import { MenuItem, Select, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';

import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-balham.css';

import Teams from 'mlb/Teams';

import GridUtils from './GridUtils';
import MlbStatsContext from './MlbStatsContext';
import styles from './MlbDahLog.module.scss';

const FILTERS = {
  MARGIN_SIDE: {
    KEY: 'marginSide',
    ANY: 'any',
    DAH: 'dah',
    UAH: 'uah',
  },
  TRI_INNING: {
    KEY: 'triInning',
    EARLY: 'early',
    MID: 'mid',
    LATE: 'late',
  },
  TEAM: {
    KEY: 'team',
    ALL: 'all',
  },
};

const TRI_INNING_TO_INNING_NUM = {
  [FILTERS.TRI_INNING.EARLY]: 1,
  [FILTERS.TRI_INNING.MID]: 4,
  [FILTERS.TRI_INNING.LATE]: 7,
};

const DEFAULT_FILTERS = {
  [FILTERS.TEAM.KEY]: FILTERS.TEAM.ALL,
  [FILTERS.MARGIN_SIDE.KEY]: FILTERS.MARGIN_SIDE.ANY,
  [FILTERS.TRI_INNING.KEY]: FILTERS.TRI_INNING.EARLY,
};

// https://www.ag-grid.com/react-data-grid/column-properties/
const DEFAULT_COL_DEF = {
  sortable: false,
  resizable: true,
  // filter: true,

  // suppressSizeToFit: true,
  // flex: 1,
  // floatingFilter: true,
  // cellDataType: false,
  cellClass: styles.gridBodyCell,
  headerClass: styles.gridHeaderCell,
  // groupsHeaderClass: styles.gridHeaderCell,
  // wrapText: true,
  // autoHeight: true,
  comparator: GridUtils.valueComparer,
};

const MlbDahLog = (props) => {
  const gridRef = useRef();
  const { data } = useContext(MlbStatsContext);
  const { teamLogs } = data;
  const [allRows, setAllRows] = useState(null);
  const [visibleRows, setVisibleRows] = useState(null);
  const [filters, setFilters] = useState({ ...DEFAULT_FILTERS });

  const grid = gridRef.current;

  const getColumnDefs = useCallback(() => {
    const columnDefs = [GridUtils.columnDefs.date, GridUtils.columnDefs.team, GridUtils.columnDefs.oppTeam, GridUtils.columnDefs.result, GridUtils.columnDefs.linescoreInnings, GridUtils.columnDefs.startingPitchers];
    return columnDefs;
  }, []);

  const [columnDefs] = useState(getColumnDefs());

  useEffect(() => {
    setAllRows(null);
    if (!_.isNil(teamLogs)) {
      let rows = [];
      for (const teamCode of _.keys(teamLogs)) {
        for (const game of teamLogs[teamCode]) {
          rows.push(game);
        }
      }
      rows = _.orderBy(rows, ['matchup.date', 'matchup.id'], ['desc', 'desc']);
      setAllRows(rows);
    }
    return () => {
      setAllRows(null);
    };
  }, [teamLogs]);

  useEffect(() => {
    if (_.isNil(allRows)) {
      setVisibleRows(allRows);
      return;
    }
    let visibleRows = [...allRows];
    if (filters.marginSide !== FILTERS.MARGIN_SIDE.ANY) {
      const isDah = filters.marginSide === FILTERS.MARGIN_SIDE.DAH;
      const triInningStart = TRI_INNING_TO_INNING_NUM[filters.triInning];
      if (isDah) {
        visibleRows = _.filter(visibleRows, (row) => {
          const { matchup, stats } = row;
          if (matchup.homeAway !== 'away') return false;
          let awayRuns = 0;
          let homeRuns = 0;
          for (let i = 1; i < triInningStart; i++) {
            awayRuns += stats.for[`i${i}`].runs;
            homeRuns += stats.against[`i${i}`].runs;
          }
          for (let i = triInningStart; i < triInningStart + 2; i++) {
            awayRuns += stats.for[`i${i}`].runs;
            homeRuns += stats.against[`i${i}`].runs;
            if (awayRuns === homeRuns) return true;
          }
          awayRuns += stats.for[`i${triInningStart + 2}`].runs;
          return awayRuns === homeRuns;
        });
      } else {
        visibleRows = _.filter(visibleRows, (row) => {
          const { matchup, stats } = row;
          if (matchup.homeAway !== 'home') return false;
          let awayRuns = 0;
          let homeRuns = 0;
          for (let i = 1; i < triInningStart; i++) {
            awayRuns += stats.against[`i${i}`].runs;
            homeRuns += stats.for[`i${i}`].runs;
          }
          for (let i = triInningStart; i < triInningStart + 2; i++) {
            awayRuns += stats.against[`i${i}`].runs;
            homeRuns += stats.for[`i${i}`].runs;
            if (awayRuns === homeRuns) return true;
          }
          awayRuns += stats.against[`i${triInningStart + 2}`].runs;
          return awayRuns === homeRuns;
          // return stats.against[`i${triInningStart}`].runs === 0;
        });
      }
    }
    if (filters[FILTERS.TEAM.KEY] !== FILTERS.TEAM.ALL) {
      visibleRows = _.filter(visibleRows, (row) => {
        return row.matchup.team === filters[FILTERS.TEAM.KEY];
      });
    }
    setVisibleRows(visibleRows);
  }, [allRows, filters, setVisibleRows]);

  const onGridReady = () => {};

  const onFirstDataRendered = useCallback(() => {
    // grid?.api.autoSizeAllColumns();
    // setTimeout(() => {
    //   grid?.api.autoSizeAllColumns();
    // }, 200);
  }, []);

  const onFilterTeamChange = (newValue) => {
    setFilters(_.merge({}, filters, { [FILTERS.TEAM.KEY]: newValue }));
  };
  const onFilterMarginSideChange = (event, newValue) => {
    if (!newValue) return;
    setFilters(_.merge({}, filters, { [FILTERS.MARGIN_SIDE.KEY]: newValue }));
  };
  const onFilterTriInningChange = (event, newValue) => {
    if (!newValue) return;
    setFilters(_.merge({}, filters, { [FILTERS.TRI_INNING.KEY]: newValue }));
  };

  const onCellClicked = async (event) => {
    const gridApi = grid?.api;
    if (!gridApi) return;
    if (!_.includes(['team', 'oppTeam'], event?.colDef?.colId)) return;

    onFilterTeamChange(event.value?.teamCode);
  };

  const renderTeamOptions = () => {
    const teamOptions = _.map(Teams.TEAMS_BY_CODE, (team) => {
      const logo = <img className={styles.teamLogo} src={`/assets/mlb/logos/${team.code}.svg`} alt='logo' />;
      return (
        <MenuItem key={`teamOption[${team.code}]`} className={styles.teamOption} value={team.code}>
          <div className={styles.teamOptionContent}>
            {logo}
            <div className={styles.teamCode}>{team.code}</div>
          </div>
        </MenuItem>
      );
    });

    return (
      <Select
        variant='outlined'
        id='team-select'
        classes={{ select: styles.teamSelect }}
        value={filters.team}
        onChange={(event) => {
          onFilterTeamChange(event.target.value);
        }}>
        <MenuItem className={styles.teamOption} value={FILTERS.TEAM.ALL}>
          All Teams
        </MenuItem>
        {teamOptions}
      </Select>
    );
  };

  let triInningFilter = null;
  if (filters.marginSide !== FILTERS.MARGIN_SIDE.ANY) {
    triInningFilter = (
      <Tooltip title='Filter games based on Inning Groups... Early (1-3), Mid (4-6), Late (7-9)'>
        <ToggleButtonGroup classes={{ grouped: styles.filterButtons }} size='small' color='primary' value={filters.triInning} exclusive onChange={onFilterTriInningChange}>
          <ToggleButton classes={{ selected: styles.filterButtonSelected }} value={FILTERS.TRI_INNING.EARLY}>
            Early
          </ToggleButton>
          <ToggleButton classes={{ selected: styles.filterButtonSelected }} value={FILTERS.TRI_INNING.MID}>
            Mid
          </ToggleButton>
          <ToggleButton classes={{ selected: styles.filterButtonSelected }} value={FILTERS.TRI_INNING.LATE}>
            Late
          </ToggleButton>
        </ToggleButtonGroup>
      </Tooltip>
    );
  }

  return (
    <div className={cx(styles.container, props.className)}>
      <div className={styles.filtersContainer}>
        {renderTeamOptions()}
        <Tooltip
          title={
            <>
              Filter games...
              <br />
              Any (no filter).
              <br />
              DAH (team has 3 fewer outs remaining and is tied at end of top-half)
              <br />
              UAH (team has 3 extra outs remaining and is tied at end of top-half)
            </>
          }>
          <ToggleButtonGroup classes={{ grouped: styles.filterButtons }} size='small' color='primary' value={filters.marginSide} exclusive onChange={onFilterMarginSideChange}>
            <ToggleButton classes={{ selected: styles.filterButtonSelected }} value={FILTERS.MARGIN_SIDE.ANY}>
              Any
            </ToggleButton>
            <ToggleButton classes={{ selected: styles.filterButtonSelected }} value={FILTERS.MARGIN_SIDE.DAH}>
              DAH
            </ToggleButton>
            <ToggleButton classes={{ selected: styles.filterButtonSelected }} value={FILTERS.MARGIN_SIDE.UAH}>
              UAH
            </ToggleButton>
          </ToggleButtonGroup>
        </Tooltip>
        {triInningFilter}
      </div>
      <div className={cx('ag-theme-balham', styles.gridContainer)}>
        <AgGridReact
          ref={gridRef}
          rowData={visibleRows}
          columnDefs={columnDefs}
          defaultColDef={DEFAULT_COL_DEF}
          animateRows={false}
          rowHeight={40}
          rowSelection='multiple'
          suppressLoadingOverlay={true}
          onGridReady={onGridReady}
          onFirstDataRendered={onFirstDataRendered}
          onCellClicked={onCellClicked}
          autoSizeStrategy={{ type: 'fitCellContents' }}
          // debug
        />
      </div>
    </div>
  );
};

MlbDahLog.propTypes = {
  className: PropTypes.string,
};

export default MlbDahLog;
